<template>
  <Card :title="$t('passwordResetCodeInput.codeinput.title')" :footerDivider="false">
    <template v-slot:content>
      <p
        v-dompurify-html="
          $t('passwordResetCodeInput.codeinput.description.idLevel0', { username })
        "
      ></p>
      <p
        v-if="hasGeneralError"
        class="error"
        v-dompurify-html="
          $t('passwordResetCodeInput.codeinput.errors.resetCodeInformationDoesntMatch')
        "
      ></p>
      <div>
        <div id="emailcode">
          <BasicTextfield
            @input="onEmailcodeInputIsUpdated"
            id="password-reset.bestaetigungs-code-input"
            maxlength="11"
            autocomplete="off"
            pattern="^\d{3}-\d{3}-\d{3}$"
            formnovalidate="true"
            @focus="onEmailInput"
            @error="toggleEmailcodeError"
            @cleared="setEmailcodeToDefaultStatus"
            @blur="setEmailcodeToDefaultStatus"
            @keyup.enter="onSubmit"
            @keyup.delete="triggerEmailcodeDeletePressed"
            :value="emailcode"
            :label="$t('passwordResetCodeInput.emailcode.label')"
            :status="emailcodeTextfieldStatus"
            :placeholder="$t('passwordResetCodeInput.emailcode.placeholder')"
            inputmode="numeric"
            :required="true"
          >
            <template v-slot:error>{{ emailcodeErrorMessage }}</template>
          </BasicTextfield>
        </div>
      </div>
      <div>
        <div id="birthdate">
          <DateInput
            v-model="birthdate"
            id="password-reset.birthdate-input"
            autocomplete="off"
            @focus="onBirthdateInput"
            @update:input="onBirthdateInputIsUpdated"
            @update:invalid-date="notifyErrorDate"
            @keyup.enter="onSubmit"
            :status="birthdateTextfieldStatus"
            :label="$t('passwordResetCodeInput.birthdate.label')"
            :placeholder="$t('passwordResetCodeInput.birthdate.placeholder')"
            mask="DD.MM.YYYY"
            :minValidYear="18"
            :required="true"
          >
            <template v-slot:error>{{ birthdateErrorMessage }}</template>
            <template v-slot:info
              >{{ $t('passwordResetCodeInput.birthdate.info') }}
            </template>
          </DateInput>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <CustomButton
          @click="onSubmit"
          buttonId="password-reset.service-code-bestaetigen-button"
          :button-color="ButtonColor.PRIMARY"
          :pending-state="pending"
          >{{ $t('passwordResetCodeInput.submit.text') }}
        </CustomButton>
        <CustomButton
          @click="redirectToLoginOrSettingsPage"
          buttonId="password-reset.abbrechen-button"
          :buttonColor="ButtonColor.LINK"
        >
          {{ $t('passwordResetCodeInput.abortion.text') }}
        </CustomButton>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  DateInput,
  InputStatus
} from 'de-zurich-ui-library'
import { isAuthenticated } from '@/utils/KeycloakUtils'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { navigate } from '@/utils/NavigationUtils'
import { validatePasswordResetCode } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

const { t } = useI18n()

dayjs.extend(customParseFormat)

const getDateFormatISO8601 = (birthdate: string): string => {
  const momentDate: Dayjs = dayjs(birthdate, 'DD.MM.YYYY', true)
  if (momentDate.isValid()) {
    return momentDate.format('YYYY-MM-DD')
  }
  return ''
}

const props = defineProps({
  changedEmailUrl: {
    type: String,
    default: globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_EMAIL_RESET_PAGE
  },
  username: {
    type: String,
    required: true
  },
  onResetCodeReceived: {
    type: Function, //(accountId: string, resetCode: string) => void
    required: true
  }
})

const emailcode = ref('')
const emailcodeTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const emailcodeErrorMessage = ref('')
const birthdate = ref('')
const formattedBirthdate = ref<string | null>(null)
const birthdateTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const birthdateErrorMessage = ref('')
const deletePressed = ref(false)
const hasGeneralError = ref(false)
const pending = ref(false)
const loggedIn = ref(false)

onMounted(async () => {
  loggedIn.value = isAuthenticated()
})

const onBirthdateInput = (): void => {
  birthdateTextfieldStatus.value = InputStatus.DEFAULT
}

const onBirthdateInputIsUpdated = (): void => {
  birthdateTextfieldStatus.value = InputStatus.VALID
}

const onEmailInput = (): void => {
  emailcodeTextfieldStatus.value = InputStatus.DEFAULT
}

const onEmailcodeInputIsUpdated = (val: string): void => {
  emailcode.value = val
  emailcodeTextfieldStatus.value = InputStatus.VALID
  if (!deletePressed.value && /^.{3}(-.{3})?$/.test(emailcode.value)) {
    emailcode.value = emailcode.value + '-'
  }
  deletePressed.value = false

  if (emailcode.value.length > 11) {
    emailcode.value = emailcode.value.substring(0, 11)
  }
}

const toggleEmailcodeError = (): void => {
  emailcodeTextfieldStatus.value = InputStatus.ERROR
}

const setEmailcodeToDefaultStatus = (): void => {
  emailcodeTextfieldStatus.value = InputStatus.DEFAULT
}

const onSubmit = async (): Promise<void> => {
  hasGeneralError.value = false
  validateEmailcode()
  validateBirthdate()
  if (
    emailcodeTextfieldStatus.value === InputStatus.ERROR ||
    birthdateTextfieldStatus.value === InputStatus.ERROR
  ) {
    return
  }

  formattedBirthdate.value = getDateFormatISO8601(birthdate.value)
  pending.value = true

  try {
    const result = await validatePasswordResetCode(
      emailcode.value,
      props.username,
      birthdate.value ? formattedBirthdate.value || birthdate.value.trim() : undefined
    )
    // Success: kontoId is in result.data?.kontoId
    props.onResetCodeReceived(result.data?.kontoId, emailcode.value)
  } catch (e) {
    if (e instanceof ResponseError) {
      // Check for business error code E404-006-AMS
      try {
        const errorBody = await e.response.json()
        if (errorBody.code === 'E404-006-AMS') {
          hasGeneralError.value = true
        } else {
          navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
        }
      } catch {
        // Body is not JSON or cannot be parsed, treat as technical error
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      }
    } else {
      // Network or other errors
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    pending.value = false
  }
}

const redirectToLoginOrSettingsPage = (): void => {
  if (loggedIn.value) {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
  } else {
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
  }
}

const notifyErrorDate = (): void => {
  birthdateTextfieldStatus.value = InputStatus.ERROR
  birthdateErrorMessage.value = t('passwordResetCodeInput.birthdate.errors.wrongFormat')
}

const validateBirthdate = (): void => {
  if (!birthdate.value || birthdate.value.length < 10) {
    birthdateTextfieldStatus.value = InputStatus.ERROR
    birthdateErrorMessage.value = t('passwordResetCodeInput.birthdate.errors.wrongFormat')
  }
}

const triggerEmailcodeDeletePressed = (): void => {
  if (
    emailcode.value.length > 3 &&
    (emailcode.value.substring(3, 4) === '-' || emailcode.value.substring(6, 7) === '-')
  ) {
    deletePressed.value = true
  }
}

const validateEmailcode = (): void => {
  if (!emailcode.value || emailcode.value === '') {
    emailcodeTextfieldStatus.value = InputStatus.ERROR
    emailcodeErrorMessage.value = t('passwordResetCodeInput.emailcode.errors.empty')
  } else if (
    !/^[\d-]+$/.test(emailcode.value) ||
    !/^\d{3}-\d{3}-\d{3}$/.test(emailcode.value)
  ) {
    emailcodeTextfieldStatus.value = InputStatus.ERROR
    emailcodeErrorMessage.value = t(
      'passwordResetCodeInput.emailcode.errors.invalidInput'
    )
  }
}
</script>

<style scoped lang="scss">
span.error-message {
  font-size: 24px;
  color: var(--input-color-error);
}

b.error-message {
  padding-left: 5px;
  color: var(--input-color-error);
}

.card {
  min-height: 250px;
}
</style>
